import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {forkJoin, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {EquipmentResponse} from '../collectors-equipment/equipment.types';
import {SiteService} from '@shared/services';
import {BaseService} from '@shared/services/api/base-service';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {Collector, CollectorType, Equipment, Level} from '@app/graphql-typings/graphql';
import {EquipmentScopeOptions, EquipmentService} from '@shared/services/api/equipment.service';
import {CommissionDataNavigationState} from "@app/layout/sitemanager/types/types";
import {RoutingStateService} from "@shared/services/routing/routing-state.service";
import {SiteInfo} from "@shared/services/api/access-sites.interface";
import {NavigationCacheService} from "@app/layout/sitemanager/services/navigation-cache.service";
import {StringUtils} from '@shared/util/general/string-utils';

export const collectorEquipmentCloneResolver: ResolveFn<EquipmentResponse> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<EquipmentResponse> => {
  const routingStateService: RoutingStateService = inject(RoutingStateService);
  const navigationState: CommissionDataNavigationState = routingStateService.getNavigationState<CommissionDataNavigationState>();
  const equipmentId: number = StringUtils.stringToNumber(route.paramMap.get('id'));
  const siteId: number = StringUtils.stringToNumber(route.paramMap.get('site-id'));
  const collectorType: CollectorType = route.data.collector_type;
  return inject(CollectorEquipmentCloneService).getEquipmentData(siteId, collectorType, equipmentId, navigationState);
};

@Injectable({
  providedIn: 'root'
})
export class CollectorEquipmentCloneService extends BaseService {
  private readonly logger: AceLogger;

  constructor(private siteService: SiteService,
              private equipmentService: EquipmentService,
              private navigationCacheService: NavigationCacheService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(CollectorEquipmentCloneService.name);
  }

  getEquipmentData(siteId: number, collectorType: CollectorType, equipmentId = 0, navigationState: CommissionDataNavigationState): Observable<EquipmentResponse> {
    const equipmentScopeOptions: EquipmentScopeOptions = {
      getFavouritesData: true,
      getDeviceData: collectorType === CollectorType.Physical
    };

    const equipment$ = this.equipmentService.getEquipmentById(equipmentId, equipmentScopeOptions);
    const collector$ = navigationState?.collector
      ? of(navigationState.collector)
      : this.navigationCacheService.getSiteCollector(siteId, collectorType);
    const site$ = this.siteService.getCachedSiteInfo(siteId);
    const levels$ = navigationState?.levels
      ? of(navigationState.levels)
      : this.navigationCacheService.getSiteLevels(siteId);
    return forkJoin([site$, collector$, equipment$, levels$])
        .pipe(map(([site, collector, equipment, levels]: [SiteInfo, Collector, Equipment, Level[]]) => {
            return {site, collector, equipment, levels};
        }));
  }


}
